import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  User, Mail, Phone, Building, Briefcase, Instagram, Link as LinkIcon,
  Edit3, Check, X, ChevronRight, Save, MapPin, Loader2
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default role options - host-relevant labels (R25). Stale tech-startup labels removed.
// API can override with category-specific options at runtime.
export const ROLE_OPTIONS = [
  { id: "host", label: "Host" },
  { id: "client", label: "Client" },
  { id: "guest", label: "Guest" },
  { id: "team", label: "Team" },
  { id: "press", label: "Press" },
  { id: "other", label: "Other" }
];

// City suggestions (common cities)
const POPULAR_CITIES = [
  "Los Angeles, CA",
  "New York, NY",
  "Miami, FL",
  "San Francisco, CA",
  "Chicago, IL",
  "Atlanta, GA",
  "Dallas, TX",
  "Houston, TX",
  "Seattle, WA",
  "Denver, CO",
  "Austin, TX",
  "Las Vegas, NV",
  "Phoenix, AZ",
  "Portland, OR",
  "San Diego, CA"
];

// About Edit Modal
export const AboutEditModal = ({ user, onSave, onClose, category = "events" }) => {
  const [roleOptions, setRoleOptions] = useState(ROLE_OPTIONS);
  const [roleOptionsLoading, setRoleOptionsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    city: user?.city || "",
    company: user?.company || "",
    title: user?.title || "",
    role: user?.role_type || user?.role_title || "",
    instagram: user?.instagram || user?.instagram_username || "",
    tiktok: user?.tiktok || "",
    linkedin: user?.linkedin || ""
  });
  const [saving, setSaving] = useState(false);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [filteredCities, setFilteredCities] = useState(POPULAR_CITIES);

  // Fetch dynamic role options based on category
  useEffect(() => {
    const fetchRoleOptions = async () => {
      try {
        const categoryToFetch = category || "events";
        const res = await fetch(`${API}/role-options/by-category/${categoryToFetch}`);
        if (res.ok) {
          const data = await res.json();
          if (data.options && data.options.length > 0) {
            setRoleOptions(data.options);
          }
        }
      } catch (err) {
        console.error("Failed to fetch role options:", err);
      } finally {
        setRoleOptionsLoading(false);
      }
    };
    fetchRoleOptions();
  }, [category]);

  const handleCityChange = (value) => {
    setFormData({...formData, city: value});
    if (value.length > 0) {
      const filtered = POPULAR_CITIES.filter(city => 
        city.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCities(filtered.length > 0 ? filtered : POPULAR_CITIES);
      setShowCitySuggestions(true);
    } else {
      setShowCitySuggestions(false);
    }
  };

  const selectCity = (city) => {
    setFormData({...formData, city});
    setShowCitySuggestions(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token") || localStorage.getItem("phygital_auth_token");
      const res = await fetch(`${API}/user/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          company: formData.company,
          title: formData.title,
          role_type: formData.role,
          role_title: formData.role,
          instagram: formData.instagram,
          tiktok: formData.tiktok,
          linkedin: formData.linkedin
        })
      });
      
      if (!res.ok) throw new Error("Failed to save");
      
      toast.success("Profile updated");
      onSave(formData);
      onClose();
    } catch (err) {
      console.error("Save error:", err);
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-purple-500" />
            Edit About Info
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Name</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="your@email.com"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Phone</label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+1 (555) 000-0000"
              />
            </div>
            <div className="relative">
              <label className="text-sm font-medium text-gray-700 block mb-1">City</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={formData.city}
                  onChange={(e) => handleCityChange(e.target.value)}
                  onFocus={() => setShowCitySuggestions(true)}
                  placeholder="Los Angeles, CA"
                  className="pl-9"
                />
              </div>
              {showCitySuggestions && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                  {filteredCities.map((city, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => selectCity(city)}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100"
                    >
                      {city}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Work Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Company</label>
              <Input
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                placeholder="Company name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Title</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Job title"
              />
            </div>
          </div>
          
          {/* Role Selection */}
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-2">What best describes you?</label>
            {roleOptionsLoading ? (
              <div className="flex items-center gap-2 text-gray-500 py-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Loading options...</span>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {roleOptions.map(option => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => setFormData({...formData, role: option.id})}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      formData.role === option.id
                        ? "bg-purple-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    data-testid={`about-role-option-${option.id}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Social Handles */}
          <div className="border-t pt-4 mt-4">
            <label className="text-sm font-medium text-gray-700 block mb-3">Social Handles</label>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                  <Instagram className="w-4 h-4 text-white" />
                </div>
                <Input
                  value={formData.instagram}
                  onChange={(e) => setFormData({...formData, instagram: e.target.value.replace('@', '')})}
                  placeholder="instagram_handle"
                  className="flex-1"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </div>
                <Input
                  value={formData.tiktok}
                  onChange={(e) => setFormData({...formData, tiktok: e.target.value.replace('@', '')})}
                  placeholder="tiktok_handle"
                  className="flex-1"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                  <LinkIcon className="w-4 h-4 text-white" />
                </div>
                <Input
                  value={formData.linkedin}
                  onChange={(e) => setFormData({...formData, linkedin: e.target.value})}
                  placeholder="linkedin_username"
                  className="flex-1"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="flex-1">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// About Info Display Card
export const AboutInfoCard = ({ user, onEdit }) => {
  const roleLabel = ROLE_OPTIONS.find(r => r.id === (user?.role_type || user?.role_title))?.label;
  
  const hasBasicInfo = user?.name || user?.email || user?.phone || user?.city;
  const hasWorkInfo = user?.company || user?.title || user?.role_type;
  const hasSocials = user?.instagram || user?.tiktok || user?.linkedin;
  
  return (
    <Card className="rounded-xl border-gray-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <User className="w-4 h-4 text-purple-500" />
            About
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onEdit} className="h-7 px-2">
            <Edit3 className="w-3 h-3 mr-1" />
            Edit
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Basic Info */}
        {hasBasicInfo && (
          <div className="space-y-2">
            {user?.name && (
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-gray-400" />
                <span className="font-medium">{user.name}</span>
              </div>
            )}
            {user?.email && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail className="w-4 h-4 text-gray-400" />
                <span>{user.email}</span>
              </div>
            )}
            {user?.phone && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone className="w-4 h-4 text-gray-400" />
                <span>{user.phone}</span>
              </div>
            )}
            {user?.city && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span>{user.city}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Work Info */}
        {hasWorkInfo && (
          <div className="pt-2 border-t space-y-2">
            {(user?.company || user?.title) && (
              <div className="flex items-center gap-2 text-sm">
                <Building className="w-4 h-4 text-gray-400" />
                <span>
                  {user.title && <span className="font-medium">{user.title}</span>}
                  {user.title && user.company && " at "}
                  {user.company && <span>{user.company}</span>}
                </span>
              </div>
            )}
            {roleLabel && (
              <div className="flex items-center gap-2 text-sm">
                <Briefcase className="w-4 h-4 text-gray-400" />
                <span className="text-purple-600">{roleLabel}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Social Links */}
        {hasSocials && (
          <div className="pt-2 border-t flex flex-wrap gap-2">
            {user?.instagram && (
              <a 
                href={`https://instagram.com/${user.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-gradient-to-r from-purple-50 to-pink-50 text-xs text-pink-600 hover:from-purple-100 hover:to-pink-100"
              >
                <Instagram className="w-3 h-3" />
                @{user.instagram.replace('@', '')}
              </a>
            )}
            {user?.tiktok && (
              <a 
                href={`https://tiktok.com/@${user.tiktok.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-100 text-xs text-gray-700 hover:bg-gray-200"
              >
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
                @{user.tiktok.replace('@', '')}
              </a>
            )}
            {user?.linkedin && (
              <a 
                href={`https://linkedin.com/in/${user.linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-blue-50 text-xs text-blue-600 hover:bg-blue-100"
              >
                <LinkIcon className="w-3 h-3" />
                in/{user.linkedin}
              </a>
            )}
          </div>
        )}
        
        {/* Empty State */}
        {!hasBasicInfo && !hasWorkInfo && !hasSocials && (
          <div className="text-center py-4">
            <p className="text-sm text-gray-500">No info added yet</p>
            <Button variant="outline" size="sm" onClick={onEdit} className="mt-2">
              <Edit3 className="w-3 h-3 mr-1" />
              Add Info
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AboutEditModal;
